import { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  TouchableWithoutFeedback,
  TextInput,
  Keyboard,
  Pressable,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import * as DocumentPicker from 'expo-document-picker';
import { readString } from 'react-native-csv';
import { Feather } from '@expo/vector-icons';
import { Wrap, Box } from 'react-native-flex-layout';
import DropDownPicker from 'react-native-dropdown-picker';
import Toast from 'react-native-root-toast';

import AppTextInput from '../components/AppTextInput.js';
import AppButton from '../components/AppButton.js';
import alert from '../components/alert.js';
import CustomText from '../components/CustomText';
import Header from '../components/Header.js';
import Footer from '../components/Footer.js';
import UserTable from '../components/UserTable.js';
import ToggleSwitch from '../components/ToggleSwitch.js';
import AppStyles from '../AppStyles';
import webStyles from '../style/webStyles.js';
import styles from '../style/screens/userManagementStyles';
import useUserManagement, { USER_MODES } from '../hooks/useUserManagement';
import { useResponsiveStyles } from '../hooks';
import { generateTemporaryPassword } from '../util/user';
import Util from '../util/util';
import { useTranslation } from '../i18n';

const UserManagement = (props) => {
  const { t } = useTranslation(['admin', 'common']);

  const { organization, admin, userAdmin } = props.route.params;

  // Use responsive styles hook for platform detection
  const { isWeb, screenWidth } = useResponsiveStyles();

  // Adjust scale for small web screens
  const scale = isWeb && screenWidth < 900 ? 0.7 : 1;
  const fullWidth = 1500;

  const {
    filteredUsers,
    editUsers,
    mode,
    showUsers,
    updatingUser,
    usersLoaded,
    username,
    password,
    setPassword,
    errors,
    displayErrors,
    submitEnabled,
    searchText,
    setSearchText,
    roleSearchOpen,
    setRoleSearchOpen,
    roleSearchValue,
    setRoleSearchValue,
    roleSearchFilter,
    setRoleSearchFilter,
    statusSearchOpen,
    setStatusSearchOpen,
    statusSearchValue,
    setStatusSearchValue,
    statusSearchFilter,
    setStatusSearchFilter,
    tierSearchOpen,
    setTierSearchOpen,
    tierSearchValue,
    setTierSearchValue,
    tierSearchFilter,
    setTierSearchFilter,
    rolesOpen,
    setRolesOpen,
    rolesFilterValue,
    setRolesFilterValue,
    rolesFilter,
    setRolesFilter,
    validOrgs,
    loadingOrg,
    OrgFilterOpen,
    setOrgFilterOpen,
    OrgFilterValue,
    setOrgFilterValue,
    OrgFilter,
    setOrgFilter,
    OrgFilterOpen2,
    setOrgFilterOpen2,
    OrgFilterValue2,
    setOrgFilterValue2,
    OrgFilter2,
    setOrgFilter2,
    OrgFilterOpen3,
    setOrgFilterOpen3,
    OrgFilterValue3,
    setOrgFilterValue3,
    OrgFilter3,
    setOrgFilter3,
    uploadErrors,
    setUploadErrors,
    fileLoading,
    setFileLoading,
    switchMode,
    enterEmail,
    createUser,
    editUser,
    deleteUser,
    postCreateUser,
    savePasswordReset,
    saveRoleUpdate,
    saveOrgUpdate,
    saveOrgAccessUpdate,
    VALID_ROLES,
  } = useUserManagement(organization);

  // Subscription preferences for new Admin users - per-org map
  // Format: { orgName: { detail: boolean, summary: boolean } }
  const [createSubscriptions, setCreateSubscriptions] = useState({});

  // Multi-select deletion state
  const [selectedForDeletion, setSelectedForDeletion] = useState(new Set());
  const [isDeletingBatch, setIsDeletingBatch] = useState(false);

  // Toggle user selection for batch deletion
  const toggleDeleteSelection = (username) => {
    setSelectedForDeletion((prev) => {
      const next = new Set(prev);
      if (next.has(username)) next.delete(username);
      else next.add(username);
      return next;
    });
  };

  // Clear selections (e.g., when switching modes)
  const clearDeleteSelections = () => setSelectedForDeletion(new Set());

  // Check if Admin role is selected for new user
  const isAdminSelected = rolesFilterValue === 'Analyst';

  // Get organization access for subscriptions (all orgs user will have access to)
  const getCreateOrgAccess = () => {
    if (OrgFilterValue3?.length > 0) return OrgFilterValue3;
    return [OrgFilterValue2 || organization];
  };

  // Sync subscription state when org access changes
  useEffect(() => {
    const orgs = getCreateOrgAccess();
    setCreateSubscriptions((prev) => {
      const updated = {};
      for (const org of orgs) {
        // Keep existing preferences or set defaults
        updated[org] = prev[org] || { detail: true, summary: false };
      }
      return updated;
    });
  }, [OrgFilterValue3, OrgFilterValue2, organization]);

  // Clear deletion selections when switching modes
  useEffect(() => {
    clearDeleteSelections();
  }, [mode]);

  // Helper to update a single org's subscription
  const updateCreateSubscription = (org, field, value) => {
    setCreateSubscriptions((prev) => ({
      ...prev,
      [org]: { ...prev[org], [field]: value },
    }));
  };

  // Custom create user handler that includes subscriptions for Admin users
  const handleCreateUser = async () => {
    // Build subscriptions object if Admin role is selected
    // Backend expects per-org format: { orgName: { detail, summary } }
    const subscriptions = isAdminSelected ? createSubscriptions : null;

    // Call the createUser function from the hook, passing subscriptions
    await createUser(subscriptions);

    // Reset subscription preferences after successful create
    const orgs = getCreateOrgAccess();
    const resetSubs = {};
    for (const org of orgs) {
      resetSubs[org] = { detail: true, summary: false };
    }
    setCreateSubscriptions(resetSubs);
  };

  // Parse boolean from CSV (case-insensitive)
  const parseCsvBoolean = (value, defaultValue = false) => {
    if (value === undefined || value === null || value === '') return defaultValue;
    const normalized = String(value).trim().toLowerCase();
    return ['true', 'yes', '1'].includes(normalized);
  };

  // Build per-org subscriptions from parallel lists
  const buildPerOrgSubscriptions = (orgs, detailList, summaryList) => {
    if (!orgs || orgs.length === 0) return null;

    const subscriptions = {};
    orgs.forEach((org, index) => {
      subscriptions[org] = {
        detail: parseCsvBoolean(detailList?.[index], true),   // default true
        summary: parseCsvBoolean(summaryList?.[index], false) // default false
      };
    });
    return subscriptions;
  };

  const bulkCreateUsers = async () => {
    setFileLoading(true);
    try {
      const res = await DocumentPicker.getDocumentAsync({ type: 'text/csv' });

      if (res.canceled) {
        setFileLoading(false);
        return;
      }

      const response = await fetch(res.assets[0].uri);
      const csv = await response.text();
      const results = readString(csv);

      let csvPassword = null; // Password from CSV (only used in dev mode)
      let defaultOrg = organization;
      let organization_access = [];
      let success_count = 0;
      let error_count = 0;
      let error_message = null;

      for (let row of results.data) {
        let new_user = row[0]?.trim();
        if (!new_user) continue;

        let new_role = row[1]?.trim();
        new_role =
          new_role && new_role.toLowerCase() in VALID_ROLES
            ? VALID_ROLES[new_role.toLowerCase()]
            : 'Reporter';

        // In dev mode, allow password from CSV; in production, always auto-generate
        if (__DEV__ && row[2]) {
          csvPassword = row[2];
        }

        defaultOrg = row[3] ? row[3].trim() : defaultOrg;
        if (!validOrgs.includes(defaultOrg) && organization !== defaultOrg) {
          defaultOrg = organization;
        }

        if (row[4]) {
          const temp_organization_access = row[4].split(',');
          if (temp_organization_access.every((val) => validOrgs.includes(val))) {
            organization_access = temp_organization_access;
          }
        }
        // Always ensure defaultOrg is in organization_access
        if (!organization_access.includes(defaultOrg)) {
          organization_access.push(defaultOrg);
        }

        // Parse per-org subscription preferences (columns 5 and 6)
        // Format: comma-separated booleans matching org_access order
        const detailList = row[5] ? row[5].split(',').map(v => v.trim()) : [];
        const summaryList = row[6] ? row[6].split(',').map(v => v.trim()) : [];

        // Determine orgs for subscriptions (use organization_access if set, else defaultOrg)
        const subsOrgs = organization_access.length > 0 ? organization_access : [defaultOrg];

        // Build per-org subscriptions (only for Analyst role)
        const subscriptions = new_role === 'Analyst'
          ? buildPerOrgSubscriptions(subsOrgs, detailList, summaryList)
          : null;

        // Auto-generate password if not provided (or always in production)
        const temporary_password = (__DEV__ && csvPassword) ? csvPassword.trim() : generateTemporaryPassword();

        try {
          await postCreateUser(
            new_user,
            temporary_password,
            new_user,
            new_role,
            defaultOrg,
            organization_access,
            subscriptions,
          );
          success_count += 1;
        } catch (err) {
          error_count += 1;
          // Collect failed emails and capture the first error reason
          if (!error_message) {
            // First failure - capture the error reason
            const errorReason = err.error || err.message || t('admin:users.unknownError');
            error_message = `${new_user} (${errorReason})`;
          } else {
            error_message = error_message + ', ' + new_user;
          }
        }
      }

      setUploadErrors(error_message);
      alert(t('admin:users.bulkUploadSummary', { created: success_count, errors: error_count }));
      if (success_count > 0) {
        Toast.show(`✅ ${t('admin:users.bulkCreated', { count: success_count })}`, {
          backgroundColor: '#259e23',
          position: Toast.positions.BOTTOM,
          duration: 2500,
          textColor: '#fff',
        });
      }
    } catch (err) {
      alert(t('admin:users.errorTitle'), `❌ ${t('admin:users.bulkCreateFailed')}\n`);
    }
    setFileLoading(false);
  };

  // Batch delete selected users with confirmation
  const deleteSelectedUsers = () => {
    const userList = Array.from(selectedForDeletion);
    if (userList.length === 0) return;

    const userListText = userList.map((u) => `• ${u}`).join('\n');
    const message = t('admin:users.confirmDelete', { count: userList.length, list: userListText });

    alert(
      t('admin:users.confirmDeleteTitle'),
      message,
      [
        { text: t('common:actions.cancel'), style: 'cancel' },
        {
          text: t('common:actions.delete'),
          style: 'destructive',
          onPress: async () => {
            setIsDeletingBatch(true);
            let successCount = 0;
            let errorCount = 0;

            for (const username of userList) {
              try {
                await Util.post('UserManagement', '/users/delete', {
                  body: { username, organization },
                });
                deleteUser(username);
                successCount++;
              } catch (err) {
                errorCount++;
              }
            }

            setIsDeletingBatch(false);
            clearDeleteSelections();

            if (successCount > 0) {
              Toast.show(t('admin:users.deleted', { count: successCount }), {
                backgroundColor: '#259e23',
                position: Toast.positions.BOTTOM,
                duration: 2500,
                textColor: '#fff',
              });
            }
            if (errorCount > 0) {
              alert(t('admin:users.errorTitle'), t('admin:users.deleteFailed', { count: errorCount }));
            }
          },
        },
      ],
    );
  };

  const resetSearch = () => setSearchText('');

  const isEditMode = mode === USER_MODES.edit;
  const isCreateMode = mode === USER_MODES.create;

  return (
    <View style={webStyles.container}>
      <SafeAreaView style={styles.safeAreaContainer}>
        <Header organization={organization} admin={admin} userAdmin={userAdmin} />
        <TouchableWithoutFeedback
          onPress={!isWeb ? Keyboard.dismiss : () => {}}
          accessible={false}
        >
          <View style={styles.container}>
            {/* Mode Tabs */}
            <View style={styles.chip_container}>
              <Wrap shouldWrapChildren="true" spacing={6}>
                <Pressable
                  onPress={() => switchMode(USER_MODES.create)}
                  style={{ cursor: 'pointer' }}
                >
                  {({ hovered, focused }) => (
                    <Box
                      style={[
                        isCreateMode ? styles.ChipSelected : styles.Chip,
                        !isCreateMode && (hovered || focused) && styles.ChipHovered,
                      ]}
                    >
                      <CustomText style={styles.ChipText} font="medium" size={18} color="#000">
                        {t('admin:users.createUser')}
                      </CustomText>
                    </Box>
                  )}
                </Pressable>
                <Pressable
                  onPress={() => switchMode(USER_MODES.edit)}
                  style={{ cursor: 'pointer' }}
                >
                  {({ hovered, focused }) => (
                    <Box
                      style={[
                        isEditMode ? styles.ChipSelected : styles.Chip,
                        !isEditMode && (hovered || focused) && styles.ChipHovered,
                      ]}
                    >
                      <CustomText style={styles.ChipText} font="medium" size={18} color="#000">
                        {t('admin:users.editUser')}
                      </CustomText>
                    </Box>
                  )}
                </Pressable>
              </Wrap>
            </View>

            {/* Create User Form */}
            {isCreateMode && (
              <View style={styles.formCard}>
                <AppTextInput
                  value={username}
                  onChangeText={enterEmail}
                  leftIcon="email"
                  placeholder={t('admin:users.emailPlaceholder')}
                  autoCapitalize="none"
                  keyboardType="email-address"
                  textContentType="emailAddress"
                  autoComplete="new-password"
                  fullWidth
                />
                <CustomText style={styles.error}>
                  {displayErrors
                    ? errors.email
                    : (usersLoaded && errors.email === t('admin:users.userExists') ? errors.email : null)}
                </CustomText>

                {/* Password field only shown in dev mode for testing with fake emails */}
                {__DEV__ && (
                  <>
                    <AppTextInput
                      value={password}
                      onChangeText={setPassword}
                      leftIcon="lock"
                      placeholder={t('admin:users.passwordPlaceholder')}
                      autoCapitalize="none"
                      autoCorrect={false}
                      secureTextEntry
                      textContentType="password"
                      autoComplete="new-password"
                      fullWidth
                    />
                    <CustomText style={styles.error}>{displayErrors ? errors.password : null}</CustomText>
                  </>
                )}

                <View style={screenWidth >= 1000 ? styles.dropdownWeb : styles.dropdownMobile}>
                  <DropDownPicker
                    zIndex={9}
                    multiple={false}
                    min={0}
                    open={rolesOpen}
                    value={rolesFilterValue}
                    items={rolesFilter}
                    setOpen={setRolesOpen}
                    setValue={setRolesFilterValue}
                    setItems={setRolesFilter}
                    placeholder={t('admin:users.keepCurrentRole')}
                    dropDownDirection="TOP"
                    maxHeight={158}
                  />
                  <CustomText size={12}>{t('admin:users.assignRoles')}</CustomText>
                </View>

                {OrgFilter2?.length > 1 && (
                  <View style={screenWidth >= 1000 ? styles.orgRowWeb : styles.orgRowMobile}>
                    <View style={screenWidth >= 1000 ? styles.orgDropdownLeftWeb : styles.orgDropdownLeftMobile}>
                      <DropDownPicker
                        zIndex={-1}
                        multiple={false}
                        min={0}
                        open={OrgFilterOpen2}
                        value={OrgFilterValue2}
                        items={OrgFilter2}
                        setOpen={setOrgFilterOpen2}
                        setValue={setOrgFilterValue2}
                        setItems={setOrgFilter2}
                        dropDownDirection="TOP"
                        placeholder={t('admin:users.filterByOrganization')}
                        maxHeight={158}
                        bottomOffset={200}
                      />
                      <CustomText size={12}>{t('admin:users.defaultOrganization')}</CustomText>
                    </View>
                    <View style={screenWidth >= 1000 ? styles.orgDropdownRightWeb : styles.orgDropdownRightMobile}>
                      <DropDownPicker
                        zIndex={-1}
                        multiple={true}
                        min={0}
                        open={OrgFilterOpen3}
                        value={OrgFilterValue3}
                        items={OrgFilter3}
                        setOpen={setOrgFilterOpen3}
                        setValue={setOrgFilterValue3}
                        setItems={setOrgFilter3}
                        dropDownDirection="TOP"
                        placeholder={t('admin:users.filterByOrganization')}
                        maxHeight={158}
                        bottomOffset={200}
                      />
                      <CustomText size={12}>{t('admin:users.assignOrgAccess')}</CustomText>
                    </View>
                  </View>
                )}

                {/* Notification subscriptions - only show when Admin role is selected */}
                {isAdminSelected && (
                  <View style={{ marginTop: 12, padding: 12, backgroundColor: '#F9FAFB', borderRadius: 8 }}>
                    <CustomText font="medium" size={14} color={AppStyles.colorSet.textPrimary}>
                      {t('admin:users.emailNotifications')}
                    </CustomText>
                    {getCreateOrgAccess().length === 1 ? (
                      // Single org - simple view
                      <>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                          <View style={{ flex: 1 }}>
                            <CustomText size={12} color={AppStyles.colorSet.textSecondary}>
                              {t('admin:users.reportNotifications')}
                            </CustomText>
                          </View>
                          <ToggleSwitch
                            value={createSubscriptions[getCreateOrgAccess()[0]]?.detail ?? true}
                            onValueChange={(val) => updateCreateSubscription(getCreateOrgAccess()[0], 'detail', val)}
                          />
                        </View>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                          <View style={{ flex: 1 }}>
                            <CustomText size={12} color={AppStyles.colorSet.textSecondary}>
                              {t('admin:users.weeklySummary')}
                            </CustomText>
                          </View>
                          <ToggleSwitch
                            value={createSubscriptions[getCreateOrgAccess()[0]]?.summary ?? false}
                            onValueChange={(val) => updateCreateSubscription(getCreateOrgAccess()[0], 'summary', val)}
                          />
                        </View>
                      </>
                    ) : (
                      // Multiple orgs - grid view
                      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                        {getCreateOrgAccess().map((org) => (
                          <View key={org} style={{ backgroundColor: '#fff', borderRadius: 6, padding: 8, minWidth: 140, flex: 1, maxWidth: '48%' }}>
                            <CustomText font="semiBold" size={12} color={AppStyles.colorSet.textPrimary} numberOfLines={1}>
                              {org}
                            </CustomText>
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                              <CustomText size={10} color={AppStyles.colorSet.textSecondary}>
                                {t('admin:users.reports')}
                              </CustomText>
                              <ToggleSwitch
                                value={createSubscriptions[org]?.detail ?? true}
                                onValueChange={(val) => updateCreateSubscription(org, 'detail', val)}
                                small
                              />
                            </View>
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
                              <CustomText size={10} color={AppStyles.colorSet.textSecondary}>
                                {t('admin:users.summary')}
                              </CustomText>
                              <ToggleSwitch
                                value={createSubscriptions[org]?.summary ?? false}
                                onValueChange={(val) => updateCreateSubscription(org, 'summary', val)}
                                small
                              />
                            </View>
                          </View>
                        ))}
                      </View>
                    )}
                  </View>
                )}

                <AppButton
                  title={t('admin:users.createUser')}
                  isBusy={updatingUser}
                  isEnabled={!updatingUser && submitEnabled}
                  onPress={handleCreateUser}
                  fullWidth
                />
                <AppButton
                  title={t('admin:users.bulkCreate')}
                  isBusy={fileLoading}
                  isEnabled={!fileLoading}
                  onPress={bulkCreateUsers}
                  fullWidth
                />
                <Pressable
                  onPress={() =>
                    alert(t('admin:users.bulkCreateHelpTitle'), t('admin:users.bulkCreateHelpBody'))
                  }
                  style={styles.bulkCreateHelp}
                  accessibilityLabel={t('admin:users.bulkCreateHelpLabel')}
                >
                  <CustomText style={styles.bulkCreateHelpText}>{t('admin:users.csvFormatGuide')}</CustomText>
                </Pressable>
                {uploadErrors && (
                  <CustomText style={styles.error}>
                    {t('admin:users.bulkCreateErrors', { errors: uploadErrors })}
                  </CustomText>
                )}
              </View>
            )}

            {/* Edit User Search */}
            {isEditMode && (
              <View style={screenWidth >= fullWidth ? styles.searchContainerWeb : styles.searchContainer}>
                <View style={screenWidth >= fullWidth ? styles.searchBoxWeb : styles.searchBoxMobile}>
                  <View style={styles.textboxContainer}>
                    <TextInput
                      autoComplete="new-item"
                      value={searchText}
                      onChangeText={setSearchText}
                      placeholder={t('admin:users.searchUsers')}
                      placeholderTextColor="rgba(0,0,0,0.35)"
                      style={styles.textboxText}
                    />
                    <Pressable style={styles.textboxResetSearch} onPress={resetSearch}>
                      <Feather name="x" size={24 * scale} color="rgba(0,0,0,0.35)" />
                    </Pressable>
                  </View>
                </View>

                <View style={screenWidth >= fullWidth ? styles.filterRowWeb : styles.filterRowMobile}>
                  <View style={screenWidth >= fullWidth ? styles.filterDropdownLeftWeb : styles.filterDropdownLeftMobile}>
                    <DropDownPicker
                      style={styles.smallDropdown}
                      textStyle={styles.smallDropdownText}
                      badgeStyle={styles.smallDropdownBadge}
                      zIndex={9}
                      multiple={true}
                      min={0}
                      open={roleSearchOpen}
                      value={roleSearchValue}
                      items={roleSearchFilter}
                      setOpen={setRoleSearchOpen}
                      setValue={setRoleSearchValue}
                      setItems={setRoleSearchFilter}
                      placeholder={t('admin:users.filterByRole')}
                      dropDownDirection="TOP"
                      maxHeight={130}
                    />
                  </View>
                  <View style={screenWidth >= fullWidth ? styles.filterDropdownRightWeb : styles.filterDropdownRightMobile}>
                    <DropDownPicker
                      style={styles.smallDropdown}
                      textStyle={styles.smallDropdownText}
                      badgeStyle={styles.smallDropdownBadge}
                      zIndex={9}
                      multiple={true}
                      min={0}
                      open={statusSearchOpen}
                      value={statusSearchValue}
                      items={statusSearchFilter}
                      setOpen={setStatusSearchOpen}
                      setValue={setStatusSearchValue}
                      setItems={setStatusSearchFilter}
                      placeholder={t('admin:users.filterByStatus')}
                      dropDownDirection="TOP"
                      maxHeight={158}
                    />
                  </View>
                </View>

                <View style={screenWidth >= fullWidth ? styles.filterRowWeb : styles.filterRowMobile}>
                  <View style={screenWidth >= fullWidth ? styles.filterDropdownLeftWeb : styles.filterDropdownLeftMobile}>
                    <DropDownPicker
                      style={styles.smallDropdown}
                      textStyle={styles.smallDropdownText}
                      badgeStyle={styles.smallDropdownBadge}
                      zIndex={8}
                      multiple={true}
                      min={0}
                      open={tierSearchOpen}
                      value={tierSearchValue}
                      items={tierSearchFilter}
                      setOpen={setTierSearchOpen}
                      setValue={setTierSearchValue}
                      setItems={setTierSearchFilter}
                      placeholder={t('admin:users.filterByTier')}
                      dropDownDirection="TOP"
                      maxHeight={158}
                    />
                  </View>
                </View>

                {/* Organization Filter */}
                {OrgFilter?.length > 1 && (
                  <View style={screenWidth >= fullWidth ? styles.orgFilterWeb : styles.orgFilterMobile}>
                    <DropDownPicker
                      style={styles.smallDropdown}
                      textStyle={styles.smallDropdownText}
                      badgeStyle={styles.smallDropdownBadge}
                      loading={loadingOrg}
                      zIndex={8}
                      multiple={true}
                      min={0}
                      open={OrgFilterOpen}
                      value={OrgFilterValue}
                      items={OrgFilter}
                      setOpen={setOrgFilterOpen}
                      setValue={setOrgFilterValue}
                      setItems={setOrgFilter}
                      dropDownDirection="TOP"
                      placeholder={t('admin:users.filterByOrganization')}
                      maxHeight={158}
                    />
                  </View>
                )}
              </View>
            )}

            {/* User Table - only show in Edit mode */}
            {isEditMode && (
              <ScrollView
                key={`${searchText}-${roleSearchValue.join('-')}-${statusSearchValue.join('-')}-${tierSearchValue.join('-')}-${OrgFilterValue.join('-')}-${Object.keys(filteredUsers).length}`}
                style={screenWidth >= fullWidth ? styles.tableScrollWeb : styles.tableScrollMobile}
              >
                {showUsers && (
                  <UserTable
                    filteredUsers={filteredUsers}
                    isEditMode={isEditMode}
                    validOrgs={validOrgs}
                    editUsers={editUsers}
                    searchText={searchText}
                    roleSearchValue={roleSearchValue}
                    statusSearchValue={statusSearchValue}
                    OrgFilterValue={OrgFilterValue}
                    onSaveRoleUpdate={saveRoleUpdate}
                    onSaveOrgUpdate={saveOrgUpdate}
                    onSaveOrgAccessUpdate={saveOrgAccessUpdate}
                    onSavePasswordReset={savePasswordReset}
                    onEditUser={editUser}
                    onDeleteUser={deleteUser}
                    selectedForDeletion={selectedForDeletion}
                    onToggleDeleteSelection={toggleDeleteSelection}
                    onDeleteSelected={deleteSelectedUsers}
                    usersLoaded={usersLoaded}
                  />
                )}
              </ScrollView>
            )}
          </View>
        </TouchableWithoutFeedback>
      </SafeAreaView>
      <Footer admin={admin} organization={organization} userAdmin={userAdmin} />
    </View>
  );
};

export default UserManagement;
